/**
 * Renders the player component
 */

import React from 'react';
import { observer } from 'mobx-react';

// Helpers
import { getParam } from '../../../helpers/helpers';

// Translation
import translate from '../../translate/Translate';

// Components
import Video from './Video';
import Playbar from './Playbar';
import SessionDetails from '../session/SessionDetails';
import Report from './Report';

const Player = observer(class Player extends React.Component {
  constructor() {
    super();

    // Set the initial state
    this.state = {
      complete: false,
      current: 0,
      duration: 0,
      isFullScreen: false,
      isMuted: false,
      isPlaying: false,
      playSpeed: 1,
      report: false,
      seeked: false,
      volume: 1
    };

    // Bind this to functions
    this.toggleDetails = this.toggleDetails.bind(this);
    this.toggleFullScreen = this.toggleFullScreen.bind(this);
    this.toggleMute = this.toggleMute.bind(this);
    this.togglePlay = this.togglePlay.bind(this);
    this.togglePlaySpeed = this.togglePlaySpeed.bind(this);
    this.handleReplay = this.handleReplay.bind(this);
    this.handleReport = this.handleReport.bind(this);
    this.handleSeek = this.handleSeek.bind(this);
    this.handleVolume = this.handleVolume.bind(this);

    // Set up the timer
    this.timer = null;
  }

  componentDidMount() {
    const { PlayerStore } = this.props.store;
    const url = window.location.href;
    const installSid = getParam('installSid', url);
    const examSid = getParam('examSid', url);
    const sessionSid = getParam('sessionSid', url);

    if (installSid && examSid && sessionSid) {
      PlayerStore.getSession(installSid, examSid, sessionSid);
    } else {
      // TEMP hardcoded session
      PlayerStore.getSession(
        'AI2468acd6177c43f6897e0d44eac7411f',
        'EXf0e69447df6c46bfb616506a149cd17e',
        'ES084db740db758856e090c9b603e90cd4'
      )
    }
  }

  componentDidUpdate(prevProps, prevState) {
    const { PlayerStore } = this.props.store;

    if (this.state.isPlaying && !prevState.isPlaying) {
      this.startTimer();
    } else if (!this.state.isPlaying) {
      this.pauseTimer();
    }

    if (PlayerStore.forcePlay) {
      this.togglePlay();
      PlayerStore.forcePlay = false;
    }
  }

  startTimer() {
    const _this = this;
    const { playSpeed } = this.state;

    if (this.timer) {
      clearInterval(this.timer);
    }

    const vidEl = document.querySelectorAll('video')[0];

    this.timer = setInterval(() => {
      if (_this.state.current < _this.state.duration) {
        _this.setState({
          current: vidEl ? vidEl.currentTime : 0,
          seeked: false
        });
      } else {
        _this.pauseTimer();
        _this.togglePlay();
        _this.setState({
          complete: true
        });
      }
    }, 50 / playSpeed);
  }

  pauseTimer() {
    clearInterval(this.timer);
  }

  toggleDetails(props) {
    const { translation } = this.props;
    const { AppStore } = this.props.store;

    if (!AppStore.drawerContent) {
      AppStore.openDrawer(SessionDetails, translation.video.info, props);
    } else {
      AppStore.closeDrawer();
    }
  }

  toggleFullScreen() {
    this.setState({
      isFullScreen: !this.state.isFullScreen
    }, () => {
      if (this.state.isFullScreen) {
        try {
          document.body.requestFullscreen();
        } catch {
          document.body.webkitRequestFullscreen();
        }
      } else {
        try {
          document.exitFullscreen();
        } catch {
          document.webkitExitFullscreen();
        }
      }
    });
  }

  toggleMute() {
    this.setState({
      isMuted: !this.state.isMuted,
      volume: this.state.isMuted ? 1 : 0
    });
  }

  togglePlay() {
    this.setState({
      isPlaying: !this.state.isPlaying
    });
  }

  togglePlaySpeed(e) {
    this.setState({
      playSpeed: e.target.value
    }, () => {
      if (this.state.isPlaying) {
        this.startTimer();
      }
    });
  }

  handleReplay() {
    const _this = this;

    this.setState({
      complete: false,
      current: 0,
      seeked: true
    }, () => {
      _this.togglePlay();
    });
  }

  handleReport(session) {
    const { current } = this.state;
    const { AppStore, PlayerStore } = this.props.store;

    if (this.state.isPlaying) {
      this.togglePlay();
    }

    PlayerStore.session = session;

    AppStore.showModal({
      component: Report,
      props: {
        current,
        onCancel: AppStore.dismissModal
      }
    });
  }

  handleSeek(current) {
    this.setState({
      complete: current >= this.state.duration,
      current,
      seeked: true
    });
  }

  handleVolume(volume) {
    this.setState({
      isMuted: parseFloat(volume) === 0 ? true : false,
      volume
    });
  }

  prepareFlags(annotations, component) {
    const flags = annotations.filter(annotation => {
      return annotation.type === 'flag' || annotation.type === 'manual' || annotation.type === 'auto'
    });

    component.setState({
      flags
    });
  }

  prepareHighlights(annotations, component) {
    const highlights = [];

    const roomScanStart = annotations.filter(annotation => {
      return annotation.type === 'roomScanStarted';
    })[0];

    const roomScanEnd = annotations.filter(annotation => {
      return annotation.type === 'roomScanEnded';
    })[0];

    if (roomScanStart && roomScanEnd) {
      highlights.push({
        start: roomScanStart.sessionTime,
        end: roomScanEnd.sessionTime,
        title: 'Room Scan',
        type: 'roomScan'
      });
    }

    const examStart = annotations.filter(annotation => {
      return annotation.type === 'passwordPasted';
    })[0];

    const examEnd = annotations.filter(annotation => {
      return annotation.type === 'sessionEnded';
    })[0];

    if (examStart && examEnd) {
      highlights.push({
        start: examStart.sessionTime,
        end: examEnd.sessionTime,
        title: 'Exam',
        type: 'exam'
      });
    }

    component.setState({
      highlights
    });
  }

  render() {
    const { translation } = this.props;
    const {
      complete,
      current,
      duration,
      isFullScreen,
      isMuted,
      isPlaying,
      playSpeed,
      // report,
      seeked,
      volume
    } = this.state;
    const { AppStore, PlayerStore } = this.props.store;
    const { sessions } = PlayerStore;

    if (AppStore.loadingCalls.indexOf('getSession') > -1) {
      return null;
    }

    return (
      <main
        className="page player"
        onContextMenu={e => e.preventDefault()}
      >
        <section className="player__media">
          {sessions.map((session, i) =>
            <Video
              {...this.props}
              translation={translation.video}
              key={i}
              session={session}
              solo={sessions.length === 1}
              videos={session.automated.mediaFiles}
              current={current}
              duration={duration}
              isMuted={isMuted}
              isPlaying={isPlaying}
              playSpeed={playSpeed}
              onIncident={this.handleReport}
              onInfo={this.toggleDetails}
              onInit={(seconds) => {
                (seconds > duration && isFinite(seconds)) && this.setState({ duration: seconds });
              }}
              onPlay={this.togglePlay}
              onSeek={this.handleSeek}
              volume={volume}
              seeked={seeked}
              prepareFlags={this.prepareFlags}
              prepareHighlights={this.prepareHighlights}
            />
          )}
        </section>

        <Playbar
          {...this.props}
          translation={translation.playbar}
          session={sessions.length === 1 && sessions[0]}
          richPlaybar={sessions.length === 1}
          complete={complete}
          current={current}
          duration={duration}
          isFullScreen={isFullScreen}
          isMuted={isMuted}
          isPlaying={isPlaying}
          playSpeed={playSpeed}
          onFullScreen={this.toggleFullScreen}
          onMute={this.toggleMute}
          onPlay={this.togglePlay}
          onPlaySpeed={this.togglePlaySpeed}
          onVolume={this.handleVolume}
          onSeek={this.handleSeek}
          onReplay={this.handleReplay}
          volume={volume}
          prepareFlags={this.prepareFlags}
          prepareHighlights={this.prepareHighlights}
        />
      </main>
    )
  }
})

export default translate('Player')(Player);
